import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { 
  deleteUser, 
  reauthenticateWithCredential, 
  EmailAuthProvider,
  updatePassword,
} from 'firebase/auth';
import { db, auth } from '../firebase';
import styles from './AccountSettingsPage.module.css';

function AccountSettingsPage() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [accountData, setAccountData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchAccountData = async () => {
      if (!currentUser) {
        navigate('/');
        return;
      }

      try {
        // Récupérer les données du compte depuis Firestore si elles existent
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setAccountData(userDoc.data());
        }

        // Récupérer l'abonnement actif
        const subscriptionsQuery = query(
          collection(db, 'subscriptions'),
          where('userId', '==', currentUser.uid),
          where('status', '==', 'active')
        );
        const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
        if (!subscriptionsSnapshot.empty) {
          const subData = subscriptionsSnapshot.docs[0].data();
          setSubscription({
            id: subscriptionsSnapshot.docs[0].id,
            ...subData,
          });
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching account data:', error);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [currentUser, navigate]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Non disponible';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    try {
      // Supprimer les données utilisateur de Firestore
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        await deleteDoc(userDocRef);
      }

      // Supprimer les favoris
      const favoritesQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', currentUser.uid)
      );
      const favoritesSnapshot = await getDocs(favoritesQuery);
      const deleteFavoritesPromises = favoritesSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deleteFavoritesPromises);

      // Supprimer les avis
      const reviewsQuery = query(
        collection(db, 'reviews'),
        where('userId', '==', currentUser.uid)
      );
      const reviewsSnapshot = await getDocs(reviewsQuery);
      const deleteReviewsPromises = reviewsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deleteReviewsPromises);

      // Supprimer les commentaires
      const commentsQuery = query(
        collection(db, 'comments'),
        where('userId', '==', currentUser.uid)
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const deleteCommentsPromises = commentsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deleteCommentsPromises);

      // Supprimer les posts
      const postsQuery = query(
        collection(db, 'posts'),
        where('userId', '==', currentUser.uid)
      );
      const postsSnapshot = await getDocs(postsQuery);
      const deletePostsPromises = postsSnapshot.docs.map((doc) =>
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePostsPromises);

      // Supprimer le compte Firebase Auth
      await deleteUser(currentUser);
      
      await logout();
      navigate('/');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error deleting account:', error);
      setError('Erreur lors de la suppression du compte');
      setShowDeleteModal(false);
    }
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      // Réauthentifier l'utilisateur
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Mettre à jour le mot de passe
      await updatePassword(currentUser, passwordData.newPassword);

      setSuccess('Mot de passe modifié avec succès');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => {
        setShowPasswordModal(false);
        setSuccess('');
      }, 2000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error changing password:', error);
      if (error.code === 'auth/wrong-password') {
        setError('Mot de passe actuel incorrect');
      } else if (error.code === 'auth/weak-password') {
        setError('Le nouveau mot de passe est trop faible');
      } else {
        setError('Erreur lors de la modification du mot de passe');
      }
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>Gestion du compte</h1>

        {/* Section 1: Détails du compte */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Détails du compte</h2>
          <div className={styles.detailsContainer}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Pseudo</span>
              <span className={styles.detailValue}>
                {currentUser?.displayName || 'Non défini'}
              </span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Email</span>
              <span className={styles.detailValue}>{currentUser?.email}</span>
            </div>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Date de création</span>
              <span className={styles.detailValue}>
                {formatDate(currentUser?.metadata?.creationTime)}
              </span>
            </div>
          </div>
        </section>

        {/* Section 2: Actions */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Actions</h2>
          <div className={styles.actionsContainer}>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => setShowPasswordModal(true)}
            >
              Changer de mot de passe
            </button>
            <button
              type="button"
              className={`${styles.actionButton} ${styles.deleteButton}`}
              onClick={() => setShowDeleteModal(true)}
            >
              Supprimer le compte
            </button>
          </div>
        </section>

        {/* Section 3: Abonnement */}
        {subscription && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Abonnement</h2>
            <div className={styles.subscriptionContainer}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Statut</span>
                <span className={`${styles.detailValue} ${styles.statusActive}`}>
                  Actif
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Type</span>
                <span className={styles.detailValue}>
                  {subscription.plan || 'Standard'}
                </span>
              </div>
              {subscription.startDate && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Date de début</span>
                  <span className={styles.detailValue}>
                    {formatDate(subscription.startDate)}
                  </span>
                </div>
              )}
              {subscription.endDate && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Date de fin</span>
                  <span className={styles.detailValue}>
                    {formatDate(subscription.endDate)}
                  </span>
                </div>
              )}
              {subscription.amount && (
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Montant</span>
                  <span className={styles.detailValue}>
                    {subscription.amount} €
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Modal changement de mot de passe */}
        {showPasswordModal && (
          <div className={styles.modalOverlay} onClick={() => setShowPasswordModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
              <h3>Changer de mot de passe</h3>
              {error && <div className={styles.error}>{error}</div>}
              {success && <div className={styles.success}>{success}</div>}
              <div className={styles.formGroup}>
                <label htmlFor="currentPassword">Mot de passe actuel</label>
                <input
                  id="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newPassword">Nouveau mot de passe</label>
                <input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className={styles.input}
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.modalButton}
                  onClick={() => setShowPasswordModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                  onClick={handleChangePassword}
                >
                  Modifier
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal suppression de compte */}
        {showDeleteModal && (
          <div className={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={styles.modalClose}
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
              <h3>Supprimer le compte</h3>
              {subscription && (
                <div className={styles.warningBox}>
                  <strong>⚠️ Attention :</strong>
                  <p>
                    Vous avez un abonnement actif. Si vous supprimez votre compte :
                  </p>
                  <ul>
                    <li>Votre abonnement sera annulé</li>
                    <li>Vous ne pourrez plus accéder aux fonctionnalités premium</li>
                    <li>Cette action est irréversible</li>
                  </ul>
                </div>
              )}
              <p className={styles.warningText}>
                Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible
                et toutes vos données seront définitivement supprimées.
              </p>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.modalButton}
                  onClick={() => setShowDeleteModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={`${styles.modalButton} ${styles.modalButtonDanger}`}
                  onClick={handleDeleteAccount}
                >
                  Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountSettingsPage;

